import React, { useState, useEffect, useRef, useContext } from 'react';
import VoiceContext from './VoiceContext';
import VoiceDialog from '../../shared-components/VoiceDialog';
import levenshteinDistance from '../../utils/levenshteinDistance';
import { GlobalContext } from '../../GlobalContext';

const SpeechRecognitionConstructor =
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

if (!SpeechRecognitionConstructor) {
    console.error('SpeechRecognition API is not supported in this browser.');
}

type Props = {
    children?: React.ReactNode;
};

export default function VoiceProvider({ children }: Props) {
    const [isListening, setIsListening] = useState(false); // Tracks user intent for voice mode
    const [isActuallyListening, setIsActuallyListening] = useState(false); // Tracks if recognizer is actively listening
    const [microphonePermission, setMicrophonePermission] = useState<string | null>(null)
    const [transcript, setTranscript] = useState('');
    const [finalTranscript, setFinalTranscript] = useState('');
    const [spokenAnswer, setSpokenAnswer] = useState('');
    const [finalSpokenAnswer, setFinalSpokenAnswer] = useState('')
    const [recognitionStatus, setRecognitionStatus] = useState('');
    const recognitionRef = useRef<any>(null); // Use 'any' for compatibility
    const synthesisRef = useRef<SpeechSynthesis | null>(null);
    const [registeredCommands, setRegisteredCommands] = useState<string[]>([]);
    const [voiceCommand, setVoiceCommand] = useState<string>('');
    const [pausedForSpeakingCount, setPausedForSpeakingCount] = useState<number>(0);
    const registeredCommandsRef = useRef<string[]>([]);
    const questionTypesIncompatibleWithVoiceMode = ['Signature', 'Location', 'MultipleFields']

    const {setShowModal} = useContext(GlobalContext)

    const isListeningRef = useRef(false);
    useEffect(() => { isListeningRef.current = isListening; }, [isListening]);

    const pausedCountRef = useRef(0);
    useEffect(() => { pausedCountRef.current = pausedForSpeakingCount; }, [pausedForSpeakingCount]);

    useEffect(() => {
        // console.log("👄 Registered commands updated:", registeredCommands);
        registeredCommandsRef.current = registeredCommands;
    }, [registeredCommands]);


  useEffect(() => {
    let permissionStatus: any

    async function checkPermission() {
      try {
        permissionStatus = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        })
        console.log("🎤 Microphone permission status:", permissionStatus.state);
        setMicrophonePermission(permissionStatus.state)

        // Listen for changes
        permissionStatus.onchange = () => {
          setMicrophonePermission(permissionStatus.state)
        }
      } catch (err) {
        console.error("Permissions API not supported", err)
      }
    }

    checkPermission()

    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [])



    useEffect(() => {
        if (transcript) {
            setSpokenAnswer(transcript)
        }
    }, [transcript])


    useEffect(() => {
        if (finalTranscript) {
            setFinalSpokenAnswer(finalTranscript)
        }
    }, [finalTranscript])



    useEffect(() => {
        // getMicrophonePermission().then(permission => {
        //     setMicrophonePermission(permission);
        // })

        return () => {
            recognitionRef.current?.abort?.();
            recognitionRef.current?.stop();
        };
    }, []);

    useEffect(() => {
        if (recognitionRef.current) {
            recognitionRef.current.onaudiostart = () => setRecognitionStatus('🎧 Processing audio...');
            recognitionRef.current.onspeechstart = () => {
                setRecognitionStatus('🗣️ Speech detected!')
                setShowModal({ heardVoiceAnswer: 'Processing...' })
            }
            recognitionRef.current.onspeechend = () => setRecognitionStatus('🤐 Speech ended.');

        }
    }, []);

    // this is to handle when the user switches tabs or minimizes the browser then comes back
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === "visible") {
                restartIfNeeded("visibilitychange");
            }
        };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", onVis);
        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", onVis);
        };
    }, []);


    const restartIfNeeded = (reason: string) => {
        // Only restart if user intends voice mode and we're not paused for TTS
        if (!isListeningRef.current) return;
        if (pausedCountRef.current > 0) return;
        if (document.visibilityState !== "visible") return;
        if (!recognitionRef.current) return;

        // Chrome can throw InvalidStateError if you call start too fast after onend
        setTimeout(() => {
            try {
                recognitionRef.current.start();
                setIsActuallyListening(true);
                setRecognitionStatus(`🔁 Restarted (${reason})`);
            } catch (e) {
                // ignore InvalidStateError; it means it's already starting/started
                if (!(e instanceof DOMException && e.name === "InvalidStateError")) {
                    console.warn("Failed to restart recognition:", e);
                }
            }
        }, 250);
    };

    const startListeningInternal = () => {
        try {
            recognitionRef.current?.start();
            setIsActuallyListening(true); // Recognizer is actively listening
        } catch (error) {
            console.warn('Error starting speech recognition:', error);
            if (error instanceof DOMException && error.name === 'InvalidStateError') {
                // Already started, ignore
            } else {
                throw error;
            }
        }
    };

    const stopListeningInternal = () => {

        recognitionRef.current?.abort();
        setIsActuallyListening(false); // Recognizer is no longer actively listening
    };

    const startListening = (emoji = '👂') => {
        console.log(`💬 ${emoji} Starting speech recognition`);
        setIsListening(true); // User intends to enable voice mode

        if (!recognitionRef.current) {
            setUpRecognitionHandlers();
        }
        if (pausedForSpeakingCount === 0 && !isActuallyListening) {
            startListeningInternal();
        } else {
            console.log("Not starting listening because pausedForSpeakingCount is", pausedForSpeakingCount);
        }
    };

    const stopListening = (emoji = '⛔️') => {
        console.log(`💬 ${emoji} Stopping speech recognition`);
        setIsListening(false); // User intends to disable voice mode
        if (isActuallyListening) {
            stopListeningInternal();
        }
    };


    const registerCommands = (commands: string[]) => {
        // console.log('💬📣 Registering voice commands:', commands);
        setRegisteredCommands(commands);
    };

    const toggleListening = () => {
        console.log('Toggling listening state. Current state:', isListening);
        if (isListening) {
            stopListening();
        } else {
            setUpRecognitionHandlers();
            startListening('🦄');
        }
    };

    const clearVoiceCommand = () => {
        setVoiceCommand('')
    }

    const clearSpokenAnswer = () => {
        console.log("💬 Clearing spoken answer")
        setSpokenAnswer('')
        setFinalSpokenAnswer('')
    };

    const setUpRecognitionHandlers = () => {
        if (recognitionRef.current) return;
        const browser_language_code = navigator.language || 'en-US';
        console.log('Setting up speech recognition with language:', browser_language_code);

        recognitionRef.current = SpeechRecognitionConstructor
            ? new SpeechRecognitionConstructor()
            : null;
        if (!recognitionRef.current) return;
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = browser_language_code;
        recognitionRef.current.onstart = () => {
            setIsActuallyListening(true);
            setRecognitionStatus("🎙️ Listening...");
        };

        recognitionRef.current.onend = () => {
            setIsActuallyListening(false);
            setRecognitionStatus("🛑 Recognition ended");
            restartIfNeeded("onend");
        };

        recognitionRef.current.onerror = (e: any) => {
            setRecognitionStatus(`⚠️ Error: ${e?.error || "unknown"}`);
            // Some errors mean "don't auto-restart" (permission etc.)
            const fatal = ["not-allowed", "service-not-allowed", "audio-capture"];
            if (!fatal.includes(e?.error)) {
                restartIfNeeded(`onerror:${e?.error || "unknown"}`);
            }
        };

        recognitionRef.current.onresult = (event: any) => {
            // console.log("Speech recognition result event:", event);
            const lastResultIndex = event.results.length - 1; // Get the most recent result
            const lastResult = event.results[lastResultIndex];
            const alternatives = Array.from(lastResult).map((alt: any) => ({
                transcript: alt.transcript,
                confidence: alt.confidence,
            }));

            if (lastResult.isFinal) {
                setRecognitionStatus("🎙️ Processing final results...");
                const finalTranscriptText = lastResult[0].transcript.trim();
                const finalTranscriptLower = finalTranscriptText.toLowerCase();
                for (const command of registeredCommandsRef.current) {
                    // loop through words in finalTranscript and see if any match the command, allowing for some minor misrecognition using Levenshtein distance
                    for (const word of finalTranscriptLower.split(" ")) {
                        const distance = levenshteinDistance(word, command);
                        // const threshold = 1
                        const threshold = command.length <= 4 ? 0 : 1; // Allow more distance for longer commands
                        if (distance <= threshold) {
                            if (distance === 0) {
                                console.log(`💬📣✅ Command recognized: ${command}`);
                            } else {
                                console.log(`💬📣✅ Command recognized with minor misrecognition: ${command} (matched word: ${word}, distance: ${distance})`);
                            }
                            setVoiceCommand(command);
                            return; // Exit loop on first match
                        }
                    }
                }

                console.log(`💬📝✅ ${finalTranscriptText}`)
                setTranscript(finalTranscriptText); // Only set the final transcript
                setFinalTranscript(finalTranscriptText)
            } else {
                setRecognitionStatus("🎙️ Processing interim results...");
                // console.log("Processing interim results...");
                // iterate through the alternatives and see if they match an expected command
                for (const alt of alternatives) {
                    const altTranscript = alt.transcript.trim().toLowerCase();
                    // console.log("Checking alternative transcript to see if it is a command:", altTranscript);
                    if (registeredCommandsRef.current.includes(altTranscript)) {
                        // console.log(`💬👂📣 Command recognized: ${altTranscript}`);
                        // setVoiceCommand(altTranscript);
                        // return; // Exit  on first match
                    }
                }
                const interimTranscript = lastResult[0].transcript; // Only use the latest interim result


                console.log(`💬📝👂 ${interimTranscript}`)
                setTranscript(interimTranscript);

            }

            // Handle multiple alternatives


        };
    };

    const isRecognizerRunning = !!recognitionRef.current && isListening;

    interface SpeakMessageFn {
        (message: string): void;
    }

    const speakMessage = (message: string) => {
        const synth = window.speechSynthesis;
        if (!synth) return;

        const utterance = new SpeechSynthesisUtterance(message);
        //  utterance.lang = "en-US";

        utterance.onstart = () => {
            pauseListeningForSpeaking();
        };

        utterance.onend = () => {
            resumeListeningAfterSpeaking();
        };

        utterance.onerror = () => {
            // make sure we don’t get stuck paused
            resumeListeningAfterSpeaking();
        };

        synth.speak(utterance);
    };


    // log out paused for speaking count
    useEffect(() => {
        // console.log("pausedForSpeakingCount changed:", pausedForSpeakingCount);
    }, [pausedForSpeakingCount]);

    const pauseListeningForSpeaking = () => {
        if (!recognitionRef.current) return;

        if (isActuallyListening) {
            recognitionRef.current.abort?.(); // Prefer abort
            setIsActuallyListening(false); // Temporarily stop active listening
            setPausedForSpeakingCount((c) => c + 1);
        }
    };

    const resumeListeningAfterSpeaking = () => {
        setPausedForSpeakingCount((c) => {
            const next = Math.max(0, c - 1);
            if (next === 0 && isListening) {
                startListeningInternal(); // Resume listening only if user intends voice mode to be on
            }
            return next;
        });
    };

    const speechRecognitionSupported = () => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null

        return SpeechRecognition !== null
    }


    const getMicrophonePermission = async () => {
        console.log("🎤 Checking microphone permission...");
        if (navigator.permissions?.query) {
            try {
                const res = await navigator.permissions.query({
                    name: "microphone" as PermissionName,
                });
                return res.state;
            } catch { }
        }

        // fallback
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(t => t.stop());
            return "granted";
        } catch {
            return "denied";
        }
    }



    return (
        <VoiceContext.Provider
            value={{
                isListening,
                transcript,
                spokenAnswer,
                finalSpokenAnswer,
                recognitionStatus,
                startListening,
                stopListening,
                toggleListening,
                registerCommands,
                isRecognizerRunning,
                voiceCommand,
                clearVoiceCommand,
                clearSpokenAnswer,
                speakMessage,
                speechRecognitionSupported,
                questionTypesIncompatibleWithVoiceMode,
                microphonePermission,
                registeredCommands
            }}
        >


            <VoiceDialog />
            {children}
        </VoiceContext.Provider>
    );
}
